"use client";

import If from "@/components/If";
import Link from "next/link";
import type {
  HTMLAttributeAnchorTarget,
  ReactNode,
  MouseEvent,
  HTMLAttributes,
} from "react";

const COLOR_CLASSES = {
  pink: "bg-pink text-black",
  red: "bg-red text-#fefdeb",
  blue: "bg-blue text-#fefdeb",
  yellow: "bg-yellow text-black",
  green: "bg-green text-#fefdeb",
  white: "bg-#fefdeb text-black",
  black: "bg-black text-#fefdeb",
  midnight: "bg-dark-blue text-#fefdeb",
  "midnight-blue": "bg-dark-blue text-#fefdeb",
  orange: "bg-transparent text-dark-blue",
  outlineDark: "bg-transparent text-dark-blue",
  outlineLight: "bg-transparent text-white",
} as const;

const BORDER_CLASSES = {
  pink: "border-black",
  red: "border-black",
  blue: "border-black",
  yellow: "border-black",
  green: "border-green",
  white: "border-white",
  black: "border-black",
  midnight: "border-dark-blue",
  "midnight-blue": "border-dark-blue",
  orange: "border-dark-blue",
  outlineDark: "border-dark-blue",
  outlineLight: "border-white",
} as const;

type ColorKey = keyof typeof COLOR_CLASSES | (string & {});

type ButtonProps = {
  children: ReactNode;
  link?: string;
  className?: string;
  showIcon?: boolean;
  color?: ColorKey;
  target?: HTMLAttributeAnchorTarget;
  rel?: string;
  buttonType?: "button" | "submit" | "reset";
  onClick?: (event: MouseEvent<HTMLElement>) => void;
} & HTMLAttributes<HTMLElement>;

export default function Button({
  children,
  link = "",
  className = "",
  showIcon = true,
  color = "white",
  target,
  rel,
  buttonType = "button",
  onClick,
  ...rest
}: ButtonProps) {
  const computedColor = COLOR_CLASSES[color as keyof typeof COLOR_CLASSES] || COLOR_CLASSES.pink;
  const computedBorderColor =
    BORDER_CLASSES[color as keyof typeof BORDER_CLASSES] || BORDER_CLASSES.pink;
  const relValue = target === "_blank" ? rel || "noopener noreferrer" : rel;

  const renderContent = () => (
    <span className="flex items-center justify-between gap-4 z-[1] w-full">
      {children}
      <If condition={showIcon}>
        <svg
          width="24"
          height="21"
          viewBox="0 0 24 21"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="icon"
        >
          <g clipPath="url(#clip0_1_120)">
            <path
              d="M23.8883 10.476L13.1274 0L11.6146 1.47277L19.8163 9.4574H0V11.5405H19.7885L11.5868 19.5272L13.0996 21L23.8605 10.5219L23.8519 10.5135L23.8883 10.476Z"
              fill={color === "midnight" || color === "midnight-blue" ? "#fefdeb" : "#E89211"}
            />
          </g>
          <defs>
            <clipPath id="clip0_1_120">
              <rect
                width="23.8883"
                height="21"
                fill={color === "midnight" || color === "midnight-blue" ? "#0c2340" : "#E89211"}
              />
            </clipPath>
          </defs>
        </svg>
      </If>
    </span>
  );

  return (
    <>
      <If condition={Boolean(link)}>
        <Link
          href={typeof link === "string" ? link : link?.current}
          target={target}
          rel={relValue}
          onClick={(event) => onClick?.(event)}
          className={`lg:px-8 px-4 min-w-max max-w-full border button text-[1.5rem] ${computedColor} border-current-color text-border-color  flex items-center justify-between gap-4  leading-tight rounded-button ${className}`}
          {...rest}
        >
          {renderContent()}
        </Link>
      </If>
      <If condition={!link}>
        <button
          type={buttonType}
          onClick={(event) => onClick?.(event)}
          className={`lg:px-8 px-4 min-w-max max-w-full border button ${computedColor} ${computedBorderColor} text-border-color flex items-center justify-between gap-4 leading-tight rounded-button ${className}`}
          {...rest}
        >
          {renderContent()}
        </button>
      </If>
    </>
  );
}
